// src/pages/AdminPage/tabs/NotificationsTab.tsx
import { useEffect, useState, useCallback, useMemo } from "react";
import { Bell, Edit2, Trash2, Plus, ImageOff } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import SearchBar from "../components/SearchBar";
import type { NotificationItem } from "../utils/adminTypes";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface NotificationsTabProps {
  onCreate: () => void;
  onEdit: (n: NotificationItem) => void;
  onDelete: (id: number) => void;
  onError: (msg: string) => void;
}

export default function NotificationsTab({
  onCreate,
  onEdit,
  onDelete,
  onError,
}: NotificationsTabProps) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI
      .getNotifications()
      .then((res) => setItems(res.data || []))
      .catch(() => onError("Bildirishnomalarni yuklashda xatolik"))
      .finally(() => setLoading(false));
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (n) =>
        n.title.toLowerCase().includes(q) || n.text.toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <div className="fade-up">
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Sarlavha yoki matn bo'yicha qidirish..."
        />
        <button
          onClick={onCreate}
          className="btn-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "10px 16px",
            borderRadius: 12,
            border: "none",
            background: "#3B82F6",
            color: "#fff",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
          }}>
          <Plus size={15} /> Yangi bildirishnoma
        </button>
        <div
          style={{
            color: "#64748B",
            fontSize: 13,
            padding: "10px 14px",
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
          <Bell size={14} /> Jami:{" "}
          <strong style={{ color: "#fff" }}>{filtered.length}</strong>
        </div>
      </div>

      {loading ?
        <p style={{ color: "#64748B", fontSize: 13 }}>Yuklanmoqda...</p>
      : filtered.length === 0 ?
        <p style={{ color: "#64748B", fontSize: 13 }}>
          Bildirishnomalar topilmadi
        </p>
      : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((n) => (
            <div
              key={n.id}
              style={{
                display: "flex",
                gap: 12,
                padding: 14,
                background: "#111827",
                border: "1px solid #1E2D45",
                borderRadius: 14,
                alignItems: "center",
              }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  overflow: "hidden",
                  flexShrink: 0,
                  background: "#0D1526",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                {n.image_url ?
                  <img
                    src={`${IMG_BASE}/${n.image_url}`}
                    alt={n.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                : <ImageOff size={18} color="#475569" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#E2E8F0", fontWeight: 700, fontSize: 13 }}>
                  {n.title}
                </div>
                <p
                  style={{
                    color: "#64748B",
                    fontSize: 12,
                    margin: "4px 0 0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  {n.text}
                </p>
                <span style={{ color: "#475569", fontSize: 11 }}>
                  {new Date(n.createdAt).toLocaleDateString("uz-UZ")}
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => onEdit(n)}
                  title="Tahrirlash"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: "1px solid rgba(59,130,246,.3)",
                    background: "rgba(59,130,246,.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}>
                  <Edit2 size={12} color="#3B82F6" />
                </button>
                <button
                  onClick={() => onDelete(n.id)}
                  title="O'chirish"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: "1px solid rgba(239,68,68,.3)",
                    background: "rgba(239,68,68,.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}>
                  <Trash2 size={12} color="#EF4444" />
                </button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
